import { Dropdown } from "../layout/Dropdown.tsx";
import {
  computeCenterControl,
  type BoardOverlay,
  type BoardOverlayState,
} from "../../trainer/boardOverlays.ts";
import {
  centerChange,
  centerVerdict,
  guideNotes,
  type GuideInput,
} from "../../trainer/boardGuides.ts";

/** The safety and structure guides' findings in words, shown under the board. */
export function GuideNotes({
  value,
  snapshot,
}: {
  value: BoardOverlayState;
  snapshot: TrainerSnapshot;
}) {
  const notes = guideNotes(
    {
      threatened: value.threatened,
      lastMove: value.lastMove,
      loose: value.loose,
      pins: value.pins,
      pawns: value.pawns,
    },
    guideInputFor(snapshot)
  );
  if (!notes.length) return null;
  return (
    <ul className="guide-notes" aria-label="What the board guides show">
      {notes.map((note) => (
        <li key={note}>{note}</li>
      ))}
    </ul>
  );
}
import type { TrainerSnapshot } from "../../trainer/session.ts";

type Control = { key: BoardOverlay; label: string; title: string };

const SECTIONS: Array<{ heading: string; controls: Control[] }> = [
  {
    heading: "Safety",
    controls: [
      {
        key: "threatened",
        label: "Threatened",
        title:
          "Red: a piece you would lose if the opponent took it now. Orange ring: attacked, but defended enough.",
      },
      {
        key: "safe",
        label: "Safe squares",
        title:
          "When you pick up a piece: green where it is safe, a green ring where it wins material, red where you would lose material.",
      },
      {
        key: "lastMove",
        label: "Their last move",
        title: "An arrow for the opponent's move, and arrows to the pieces it newly attacks.",
      },
      {
        key: "loose",
        label: "Loose pieces",
        title:
          "Dashed rings on pieces nothing defends: orange for yours, green for theirs. Loose pieces drop off.",
      },
      {
        key: "pins",
        label: "Pins",
        title:
          "A line from the pinning piece through the pinned one to the king, queen or rook behind it.",
      },
    ],
  },
  {
    heading: "Structure",
    controls: [
      {
        key: "center",
        label: "Center control",
        title:
          "Who controls the 16 central squares: blue for White, amber for Black. Pawns count double.",
      },
      {
        key: "pawns",
        label: "Pawn structure",
        title:
          "Letters on pawns: I isolated, D doubled, P passed. Teal for yours, grey for theirs.",
      },
    ],
  },
  {
    heading: "This opening",
    controls: [
      {
        key: "hero",
        label: "Hero pieces",
        title: "Highlight the pieces that define this opening’s identity and plan.",
      },
      {
        key: "reserve",
        label: "Reserve pieces",
        title: "Fade pieces the course does not need in its next few moves.",
      },
    ],
  },
];

const CONTROLS = SECTIONS.flatMap((section) => section.controls);

/** Guides that would give the answer away while the student recalls the moves. */
export function guideAvailable(key: BoardOverlay, mode: TrainerSnapshot["mode"]): boolean {
  return !((key === "reserve" || key === "hero") && mode !== "learn");
}

/** What the guides read from the session: the position, the side, the last move. */
export function guideInputFor(snapshot: TrainerSnapshot): GuideInput {
  const last = snapshot.history.at(-1);
  return {
    fen: snapshot.fen,
    side: snapshot.playerSide,
    last: last
      ? {
          fenBefore: last.fenBefore,
          from: last.uci.slice(0, 2),
          to: last.uci.slice(2, 4),
          san: last.san,
        }
      : null,
  };
}

export function OverlayControls({
  value,
  snapshot,
  onToggle,
  onClear,
}: {
  value: BoardOverlayState;
  snapshot: TrainerSnapshot;
  onToggle: (key: BoardOverlay) => void;
  onClear: () => void;
}) {
  const mode = snapshot.mode;
  const side = snapshot.playerSide;
  const shown = (key: BoardOverlay) => value[key] && guideAvailable(key, mode);
  const enabled = CONTROLS.filter((control) => shown(control.key));
  const input = guideInputFor(snapshot);
  const center = computeCenterControl(snapshot.fen);
  const mine = side === "w" ? center.net : -center.net;
  const verdict = centerVerdict(center.net, side);
  const lastEntry = snapshot.history.at(-1);
  const change =
    input.last && lastEntry ? centerChange(input.last.fenBefore, snapshot.fen, side) : null;
  const signed = (n: number) => (n > 0 ? `+${n}` : n < 0 ? `−${-n}` : "0");

  return (
    <>
      <Dropdown
        className="board-guides"
        menuTitle="Board guides"
        label={
          <>
            Board guides
            {enabled.length ? <span className="guides-count">{enabled.length}</span> : null}
          </>
        }
      >
        <p className="dropdown-heading">See the ideas on the board</p>
        {SECTIONS.map((section) => (
          <div
            className="overlay-toolbar"
            role="group"
            aria-label={section.heading}
            key={section.heading}
          >
            <p className="guide-section-heading">{section.heading}</p>
            {section.controls.map((control) => {
              const available = guideAvailable(control.key, mode);
              const description = available
                ? control.title
                : "Off while you recall the moves: it would hint at the answer.";
              return (
                <button
                  className={`overlay-btn${shown(control.key) ? " is-on" : ""}`}
                  key={control.key}
                  type="button"
                  aria-label={control.label}
                  aria-describedby={`guide-description-${control.key}`}
                  aria-pressed={shown(control.key)}
                  disabled={!available}
                  title={description}
                  onClick={() => onToggle(control.key)}
                >
                  <span className="guide-option-label">
                    {control.label}
                    <span aria-hidden="true">{shown(control.key) ? "✓" : "+"}</span>
                  </span>
                  <span
                    className="guide-option-description"
                    id={`guide-description-${control.key}`}
                  >
                    {description}
                  </span>
                </button>
              );
            })}
          </div>
        ))}
        {enabled.length > 0 ? (
          <button type="button" className="btn-ghost btn-sm guides-clear" onClick={onClear}>
            Turn all guides off
          </button>
        ) : null}
      </Dropdown>

      {shown("center") ? (
        <div className="center-hud is-visible" aria-live="polite">
          <span className="center-hud-label">Center</span>
          <span
            className={`center-hud-score is-${verdict.tone === "you" ? (side === "w" ? "white" : "black") : verdict.tone === "them" ? (side === "w" ? "black" : "white") : "neutral"}`}
            aria-label={`Your center score ${signed(mine)}`}
          >
            {signed(mine)}
          </span>
          <span className="center-hud-desc">
            {verdict.text}
            {change !== null && change !== 0 && lastEntry
              ? ` ${lastEntry.byStudent ? "Your" : "Their"} ${lastEntry.san}: ${signed(change)}.`
              : ""}
          </span>
          <span className="center-hud-pips" aria-hidden="true">
            {(side === "w" ? [6, 5, 4, 3] : [3, 4, 5, 6]).flatMap((rank) =>
              (side === "w" ? ["c", "d", "e", "f"] : ["f", "e", "d", "c"]).map((file) => {
                const tier = center.tiers[`${file}${rank}`] || 0;
                return (
                  <span
                    key={`${file}${rank}`}
                    className={tier > 0 ? "is-white" : tier < 0 ? "is-black" : "is-neutral"}
                    style={{ "--control-tier": Math.abs(tier) } as React.CSSProperties}
                  />
                );
              })
            )}
          </span>
        </div>
      ) : null}
    </>
  );
}
